import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Logger } from "@/lib/utils/logger";

const logger = new Logger('ResetPassword');

export function ResetPassword() {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [validatingToken, setValidatingToken] = useState(true);
  const [validToken, setValidToken] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();

  // Check if we have the right parameters in the URL for password reset flow
  useEffect(() => {
    const checkResetFlow = async () => {
      try {
        setValidatingToken(true);
        
        // Get the URL hash and parameters
        const fragment = location.hash.substring(1);
        const params = new URLSearchParams(fragment || location.search);
        const accessToken = params.get('access_token');
        const refreshToken = params.get('refresh_token');
        const type = params.get('type');
        
        logger.info('Checking reset flow parameters', { hasAccessToken: !!accessToken, type });
        
        if (type === 'recovery' || accessToken) {
          // We have recovery parameters, try to exchange them for a session
          if (accessToken && refreshToken) {
            const { error } = await supabase.auth.setSession({
              access_token: accessToken,
              refresh_token: refreshToken
            });
            
            if (error) {
              logger.error('Error setting session with reset tokens', { error });
              setError('Your password reset link is invalid or has expired. Please request a new password reset.');
              setValidToken(false);
            } else {
              logger.info('Successfully set session from reset tokens');
              setValidToken(true);
            }
          } else {
            // We have type=recovery but no tokens, this is likely the hash-based flow
            setValidToken(true);
          }
        } else {
          // Check if we have an active session that can be used for password reset
          const { data, error } = await supabase.auth.getSession();
          
          if (error) {
            logger.error('Error getting session during reset flow', { error });
            setError('Unable to verify your reset link. Please try requesting a new password reset.');
            setValidToken(false);
          } else if (!data.session) {
            // No session, redirect to forgot password
            logger.warn('No valid session or reset parameters found');
            navigate('/forgot-password', { replace: true });
          } else {
            // We have a session, let user reset password
            setValidToken(true);
          }
        }
      } catch (err) {
        logger.error('Unexpected error in reset flow check', { error: err });
        setError('An unexpected error occurred. Please try again later.');
        setValidToken(false);
      } finally {
        setValidatingToken(false);
      }
    };
    
    checkResetFlow();
  }, [navigate, location]);

  const validatePassword = (pw: string): string | null => {
    if (pw.length < 8) {
      return "Password must be at least 8 characters long";
    }
    
    if (!/[A-Z]/.test(pw)) {
      return "Password must contain at least one uppercase letter";
    }
    
    if (!/[a-z]/.test(pw)) {
      return "Password must contain at least one lowercase letter";
    }
    
    if (!/[0-9]/.test(pw)) {
      return "Password must contain at least one number";
    }
    
    return null;
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Reset error state
    setError(null);
    
    // Validate passwords
    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    
    const passwordError = validatePassword(password);
    if (passwordError) {
      setError(passwordError);
      return;
    }
    
    setLoading(true);
    
    try {
      logger.info('Attempting to update password');
      
      const { error } = await supabase.auth.updateUser({ password });
      
      if (error) {
        logger.error('Failed to update password', { error });
        setError(error.message || 'Failed to update password. Please try again.');
        return;
      }
      
      logger.info('Password updated successfully');
      setSuccess(true);
      
      toast({
        title: "Password updated",
        description: "Your password has been updated successfully.",
        variant: "default"
      });
      
      // Redirect to sign in after a delay
      setTimeout(() => {
        navigate('/sign-in', { replace: true });
      }, 3000);
    } catch (err) {
      logger.error('Unexpected error updating password', { error: err });
      setError('An unexpected error occurred. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white p-6">
      <div className="max-w-md mx-auto space-y-6">
        <div className="text-center space-y-2">
          <h1 className="text-3xl font-bold">Reset Your Password</h1>
          <p className="text-gray-400">
            Enter a new password for your account
          </p>
        </div>

        {validatingToken ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-white"></div>
          </div>
        ) : error && !validToken ? (
          <Alert variant="destructive" className="bg-red-900/50 border-red-600">
            <AlertTitle>Password Reset Error</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
            <div className="mt-4">
              <Button 
                onClick={() => navigate('/forgot-password')} 
                variant="outline" 
                className="border-red-600 hover:bg-red-900/50"
              >
                Request New Reset Link
              </Button>
            </div>
          </Alert>
        ) : success ? (
          <Alert className="bg-green-900/50 border-green-600">
            <AlertTitle>Success!</AlertTitle>
            <AlertDescription>
              Your password has been updated successfully! You will be redirected to the sign in page.
            </AlertDescription>
          </Alert>
        ) : validToken ? (
          <>
            {error && (
              <Alert variant="destructive" className="bg-red-900/50 border-red-600">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <label htmlFor="password" className="block">
                  New Password
                </label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="bg-gray-900 border-gray-800 text-white"
                  placeholder="••••••••"
                  disabled={loading}
                  minLength={8}
                />
                <p className="text-xs text-gray-400 mt-1">
                  Password must be at least 8 characters and include uppercase, lowercase, and numbers
                </p>
              </div>

              <div className="space-y-2">
                <label htmlFor="confirmPassword" className="block">
                  Confirm New Password
                </label>
                <Input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  className="bg-gray-900 border-gray-800 text-white"
                  placeholder="••••••••"
                  disabled={loading}
                  minLength={8}
                />
              </div>

              <Button
                type="submit"
                className="w-full bg-white text-black hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={loading}
              >
                {loading ? "Updating..." : "Update Password"}
              </Button>
            </form>
          </>
        ) : (
          <Alert className="bg-yellow-900/50 border-yellow-600">
            <AlertTitle>Invalid Reset Link</AlertTitle>
            <AlertDescription>
              Your password reset link appears to be invalid or has expired.
            </AlertDescription>
            <div className="mt-4">
              <Button 
                onClick={() => navigate('/forgot-password')} 
                variant="outline" 
                className="border-yellow-600 hover:bg-yellow-900/50"
              >
                Request New Reset Link
              </Button>
            </div>
          </Alert>
        )}
      </div>
    </div>
  );
};
